import { difference, intersection } from 'lodash';
import { Plan, PricingVariant } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { DefaultSettingsAdapter, SettingsReader } from '../components/PackagePicker/DefaultSettingsAdapter';
import { WidgetPreset } from '../components/PackagePicker/settingsParams';
import { DEFAULT_PLAN_LIMIT } from '../constants';
import { VariantVisibilityState } from '../services/variant-visibility-storage';
import { isTruthy } from './isTruthy';
import { getIds } from './plan';
import { AnyPlan } from './plan-unified-utils';
import { VariantCategoryNameById } from './variant-categories';
import { getSelectableVariants, getVisibleVariantIds } from './variant-visibility';

export type PlanCheckboxState = 'checked' | 'unchecked' | 'indeterminate';

export function getPlanCheckboxState({
  variants,
  visibleVariantIds,
}: {
  variants: PricingVariant[];
  visibleVariantIds: Set<string>;
}): PlanCheckboxState {
  if (!variants.length) {
    return 'unchecked';
  }

  const visibleCount = variants.filter((variant) => visibleVariantIds.has(variant.id!)).length;

  if (visibleCount === variants.length) {
    return 'checked';
  }
  if (!visibleCount) {
    return 'unchecked';
  }

  return 'indeterminate';
}

export function getFullyHiddenPlanIds({
  plans,
  variantVisibilityState,
  variantCategoryNameById,
}: {
  plans: AnyPlan[];
  variantVisibilityState: VariantVisibilityState;
  variantCategoryNameById: VariantCategoryNameById;
}): string[] {
  const visibleVariantIds = getVisibleVariantIds(plans as Plan[], variantVisibilityState);
  return plans
    .filter(
      (plan) =>
        getPlanCheckboxState({
          variants: getSelectableVariants(plan as Plan, variantCategoryNameById),
          visibleVariantIds,
        }) === 'unchecked',
    )
    .map((plan) => plan.id!);
}

interface VisibilityFilter {
  filter: 'hidden' | 'visible';
  ids: string[];
}

export function sortPlansByVisibility(allPlans: AnyPlan[], { filter, ids }: VisibilityFilter) {
  return allPlans.slice().sort((planA, planB) => {
    if (ids.includes(planA.id!) && !ids.includes(planB.id!)) {
      return filter === 'visible' ? -1 : 1;
    }
    if (ids.includes(planB.id!) && !ids.includes(planA.id!)) {
      return filter === 'visible' ? 1 : -1;
    }
    return 0;
  });
}

export function getOrderedVisiblePlans(plans: AnyPlan[], settings: SettingsReader) {
  if (settings.get('preset') === WidgetPreset.SINGLE_PLAN) {
    return plans.slice(0, 1);
  }
  const orderedPlans = getOrderedPlans(plans, settings);
  return getVisiblePlans(orderedPlans, settings);
}

export function getHiddenPlanIds(plans: AnyPlan[], settings: SettingsReader): string[] {
  const ids = plans.map((p) => p.id).filter((id): id is string => !!id);
  return shouldUseVisiblePlansParam(settings)
    ? difference(ids, settings.uuidAsArray('visiblePlans'))
    : intersection(ids, settings.uuidAsArray('hiddenPlans'));
}

export function shouldUseVisiblePlansParam(settings: SettingsReader) {
  return settings.get('useVisiblePlans') || settings.uuidAsArray('visiblePlans').length > 0;
}

export function getOrderedPlans(plans: AnyPlan[], settings: SettingsReader): AnyPlan[] {
  const planOrder = settings.uuidAsArray('planOrder');
  const ordered = planOrder.map((id) => plans.find((p) => p.id === id)).filter(isTruthy);
  const unordered = plans.filter((p) => !planOrder.includes(p.id ?? ''));
  return [...ordered, ...unordered];
}

function getVisiblePlans(plans: AnyPlan[], settings: SettingsReader): AnyPlan[] {
  const hiddenPlanIds = getHiddenPlanIds(plans, settings);
  return plans.filter((p) => !hiddenPlanIds.includes(p.id ?? '')).slice(0, DEFAULT_PLAN_LIMIT);
}

export function reorderPlansToHiddenAtTheEnd({
  plans,
  variantVisibilityState,
  adaptedSettings,
  variantCategoryNameById,
}: {
  plans: AnyPlan[];
  variantVisibilityState: VariantVisibilityState;
  adaptedSettings: DefaultSettingsAdapter;
  variantCategoryNameById: VariantCategoryNameById;
}): void {
  const plansInUserOrder = getOrderedPlans(plans, adaptedSettings);
  const hiddenPlanIds = getFullyHiddenPlanIds({
    plans: plansInUserOrder,
    variantVisibilityState,
    variantCategoryNameById,
  });
  const sortedPlans = sortPlansByVisibility(plansInUserOrder, { filter: 'hidden', ids: hiddenPlanIds });

  adaptedSettings.setUuidArray('planOrder', getIds(sortedPlans.map((plan) => ({ id: plan.id! }))));
}

/**
 * Blacklist-mode only: any plan with at least one variant not in the
 * hiddenVariantsByPlan list is unhidden at the plan layer. Caller must
 * skip this in whitelist mode (isVisibleVariantsMode === true).
 */
export function unhidePlansThatHaveAnyVisibleVariant({
  plans,
  variantVisibilityState,
  adaptedSettings,
  variantCategoryNameById,
}: {
  plans: AnyPlan[];
  variantVisibilityState: VariantVisibilityState;
  adaptedSettings: DefaultSettingsAdapter;
  variantCategoryNameById: VariantCategoryNameById;
}): void {
  plans.forEach((plan) => {
    const planId = plan.id;

    if (!planId) {
      return;
    }

    const selectableVariantIds = getSelectableVariants(plan as Plan, variantCategoryNameById)
      .map((variant) => variant.id)
      .filter((id): id is string => Boolean(id));

    const hasAnyVisibleVariant = selectableVariantIds.some(
      (id) => !(variantVisibilityState.hiddenVariantsByPlan[planId] ?? []).includes(id),
    );

    if (hasAnyVisibleVariant) {
      adaptedSettings.unhidePlan(planId);
    }
  });
}
